// Pure helpers for the "Create investor" form: slug generation and validation.
// Validation returns i18n keys (not messages) so it stays testable; the modal translates.

// Mirrors the backend INVESTOR_SLUG_PATTERN: lowercase kebab-case, never UUID-shaped
// (a UUID-shaped slug would be resolved by id and be unreachable).
const SLUG_RE =
  /^(?![0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$)[a-z0-9]+(?:-[a-z0-9]+)*$/;
const SLUG_MAX_LENGTH = 100;
const MIN_PASSWORD_LENGTH = 8;
const EMAIL_RE = /^\S+@\S+\.\S+$/;

/** Derives a slug from a display name: strips accents, lowercases, hyphenates. */
export function slugify(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, SLUG_MAX_LENGTH)
    .replace(/-+$/, ''); // slicing can leave a trailing hyphen
}

export interface CreateInvestorFormValues {
  name: string;
  slug: string;
  adminName: string;
  adminLastname: string;
  adminEmail: string;
  adminPassword: string;
}

export type CreateInvestorFormErrors = Partial<Record<keyof CreateInvestorFormValues, string>>;

/** Slug-only check, used for live feedback while typing. Returns an i18n key or undefined. */
export function validateSlug(slug: string, existingSlugs: ReadonlySet<string>): string | undefined {
  if (!slug) return 'admin:investors.validation.slugRequired';
  if (slug.length > SLUG_MAX_LENGTH || !SLUG_RE.test(slug))
    return 'admin:investors.validation.slugFormat';
  if (existingSlugs.has(slug)) return 'admin:investors.validation.slugTaken';
  return undefined;
}

/** Validates the whole form; empty object means valid. */
export function validateCreateInvestor(
  values: CreateInvestorFormValues,
  existingSlugs: ReadonlySet<string>
): CreateInvestorFormErrors {
  const errors: CreateInvestorFormErrors = {};
  if (!values.name.trim()) errors.name = 'admin:investors.validation.nameRequired';

  const slugError = validateSlug(values.slug, existingSlugs);
  if (slugError) errors.slug = slugError;

  if (!values.adminName.trim()) errors.adminName = 'admin:users.validation.name';
  if (!values.adminLastname.trim()) errors.adminLastname = 'admin:users.validation.lastname';
  if (!EMAIL_RE.test(values.adminEmail.trim())) errors.adminEmail = 'admin:users.validation.email';

  if (!values.adminPassword) {
    errors.adminPassword = 'admin:users.validation.password';
  } else if (values.adminPassword.length < MIN_PASSWORD_LENGTH) {
    errors.adminPassword = 'admin:users.validation.passwordMin';
  }
  return errors;
}

// Backend conflict codes that belong to a specific form field.
const FIELD_ERRORS_BY_CODE: Record<string, CreateInvestorFormErrors> = {
  USER_ALREADY_EXISTS: { adminEmail: 'admin:users.errors.emailExists' },
  INVESTOR_SLUG_TAKEN: { slug: 'admin:investors.validation.slugTaken' },
};

/**
 * Maps an API error to inline field errors (i18n keys), or undefined when the
 * error isn't tied to a field and should be shown as a generic message.
 */
export function fieldErrorsFromApiError(error: unknown): CreateInvestorFormErrors | undefined {
  if (typeof error !== 'object' || error === null || !('code' in error)) return undefined;
  const { code } = error as { code?: unknown };
  return typeof code === 'string' ? FIELD_ERRORS_BY_CODE[code] : undefined;
}
